// Marketplace domain constants, types, and pure helpers.
// All MOCK_* arrays and getProfile/getOrder/getOffersFor fallbacks have been
// removed — the platform reads exclusively from Supabase.
//
// DEMO_ORDERS is the ONLY hardcoded data kept: three onboarding demos pinned
// at the top of Browse. They never touch the database.

import demoCharizard from "@/assets/demo-charizard.webp.asset.json";
import demoLiliana from "@/assets/demo-liliana.jpg.asset.json";
import demoLuffy from "@/assets/demo-luffy.jpg.asset.json";


export type TCG = "Pokémon" | "One Piece" | "Magic: The Gathering" | "Yu-Gi-Oh" | "Lorcana" | "Digimon";
export type Condition = "graded" | "raw";
export type RawGrade = "NM" | "LP" | "MP" | "HP" | "DMG";
export const RAW_GRADES: RawGrade[] = ["NM", "LP", "MP", "HP", "DMG"];
export const RAW_GRADE_META: Record<RawGrade, { label: string; full: string; tone: string }> = {
  NM:  { label: "NM",  full: "Near Mint",         tone: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" },
  LP:  { label: "LP",  full: "Lightly Played",    tone: "bg-amber-500/15 text-amber-400 border border-amber-500/30" },
  MP:  { label: "MP",  full: "Moderately Played", tone: "bg-orange-500/15 text-orange-400 border border-orange-500/30" },
  HP:  { label: "HP",  full: "Heavily Played",    tone: "bg-rose-500/15 text-rose-400 border border-rose-500/30" },
  DMG: { label: "DMG", full: "Damaged",           tone: "bg-destructive/20 text-destructive border border-destructive/40" },
};
export const RAW_LOW_GRADE_EXEMPT_THRESHOLD = 100;
export function isLowRawGrade(g?: RawGrade): boolean {
  return g === "LP" || g === "MP" || g === "HP" || g === "DMG";
}
export function isVerificationExempt(opts: { condition: Condition; rawGrade?: RawGrade; total: number }) {
  return opts.condition === "raw" && isLowRawGrade(opts.rawGrade) && opts.total <= RAW_LOW_GRADE_EXEMPT_THRESHOLD;
}
export type GradingCo = "PSA" | "BGS" | "CGC";
export type OrderStatus = "open" | "offers" | "accepted" | "shipped" | "delivered" | "released" | "disputed" | "cancelled";

export const TCGS: TCG[] = ["Pokémon", "One Piece", "Magic: The Gathering", "Yu-Gi-Oh", "Lorcana", "Digimon"];

/**
 * Normalize any TCG identifier (DB enum slug like "one_piece" / "mtg" or a
 * display label) into the canonical display label. Falls back to the input
 * (trimmed) if unknown — never silently coerces to "Pokémon".
 */
export function normalizeTcgLabel(input: string | null | undefined): string {
  if (!input) return "";
  const raw = String(input).trim();
  if (!raw) return "";
  const norm = raw.toLowerCase().replace(/[\s\-:!.]+/g, "_").replace(/_+/g, "_");
  const map: Record<string, TCG> = {
    pokemon: "Pokémon",
    pokémon: "Pokémon",
    one_piece: "One Piece",
    onepiece: "One Piece",
    magic_the_gathering: "Magic: The Gathering",
    magic: "Magic: The Gathering",
    mtg: "Magic: The Gathering",
    yugioh: "Yu-Gi-Oh",
    yu_gi_oh: "Yu-Gi-Oh",
    yu_gi_oh_: "Yu-Gi-Oh",
    lorcana: "Lorcana",
    digimon: "Digimon",
  };
  if (map[norm]) return map[norm];
  const hit = TCGS.find((t) => t.toLowerCase() === raw.toLowerCase());
  return hit ?? raw;
}

export type ShippingMethod = "Standard Untracked" | "Standard Tracked" | "Express Tracked" | "Overnight";
export const SHIPPING_METHODS: ShippingMethod[] = ["Standard Untracked", "Standard Tracked", "Express Tracked", "Overnight"];

export type GradeVariant = "Gem Mint" | "Pristine" | "Black Label";

export interface GradePref {
  gradingCo: GradingCo;
  minGrade: number;
  variant?: GradeVariant;
}

export interface GradeOption {
  value: string;
  grade: number;
  variant?: GradeVariant;
  label: string;
}

function buildOpts(co: GradingCo): GradeOption[] {
  const base: GradeOption[] = [];
  const lowSteps = co === "PSA" ? [1, 2, 3, 4, 5, 6, 7, 8, 9] : [1, 2, 3, 4, 5, 6, 7, 8, 8.5, 9];
  for (const g of lowSteps) base.push({ value: String(g), grade: g, label: String(g) });
  if (co === "PSA") {
    base.push({ value: "10", grade: 10, variant: "Gem Mint", label: "10 (Gem Mint)" });
  } else if (co === "CGC") {
    base.push({ value: "9.5", grade: 9.5, label: "9.5" });
    base.push({ value: "10", grade: 10, variant: "Gem Mint", label: "10 (Gem Mint)" });
    base.push({ value: "10:Pristine", grade: 10, variant: "Pristine", label: "10 (Pristine)" });
  } else if (co === "BGS") {
    base.push({ value: "9.5", grade: 9.5, variant: "Gem Mint", label: "9.5 (Gem Mint)" });
    base.push({ value: "10", grade: 10, variant: "Pristine", label: "10 (Pristine)" });
    base.push({ value: "10:Black Label", grade: 10, variant: "Black Label", label: "10 (Black Label)" });
  }
  return base;
}

export const GRADE_OPTIONS: Record<GradingCo, GradeOption[]> = {
  PSA: buildOpts("PSA"),
  BGS: buildOpts("BGS"),
  CGC: buildOpts("CGC"),
};

export function encodeGradeValue(grade: number, variant?: GradeVariant) {
  return variant ? `${grade}:${variant}` : String(grade);
}
export function decodeGradeValue(value: string): { grade: number; variant?: GradeVariant } {
  const [g, v] = value.split(":");
  return { grade: Number(g), variant: (v as GradeVariant | undefined) || undefined };
}

export function gradeLabel(co: GradingCo, grade: number, variant?: GradeVariant) {
  return variant ? `${co} ${grade} ${variant}` : `${co} ${grade}`;
}

export interface BuyOrder {
  id: string;
  shortId: string;
  buyer: string;
  tcg: TCG;
  cardName: string;
  set: string;
  cardNumber: string;
  condition: Condition;
  rawGrade?: RawGrade;
  gradingCo?: GradingCo;
  minGrade?: number;
  gradeVariant?: GradeVariant;
  altGrades?: GradePref[];
  targetPrice: number;
  marketValue?: number;
  offerCount: number;
  status: OrderStatus;
  createdAt: string;
  imageSeed: number;
  imageUrl?: string;
  preferredShipping: ShippingMethod;
  signatureRequired: boolean;
  /** Optional buyer-specified card language (english/japanese/chinese/korean/other). */
  cardLanguage?: string | null;
  buyerCity?: string;
  isDemo?: boolean;
  finalPrice?: number;
  completedAt?: string;
  seller?: string;
  /** Optional buyer rating attached at query time. */
  buyerRating?: number;
  /** Optional buyer review count attached at query time. */
  buyerReviews?: number;
}

export interface Offer {
  id: string;
  orderId: string;
  seller: string;
  sellerRating: number;
  sellerTrades: number;
  price: number;
  shippingCost: number;
  shippingMethod: ShippingMethod;
  signatureRequired: boolean;
  certNumber?: string;
  certGradingCo?: "PSA" | "BGS" | "CGC" | "GAI" | "CCIC" | "Other";
  certUrl?: string;
  photos: number;
  notes: string;
  createdAt: string;
  status: "pending" | "accepted" | "rejected";
}

export interface UserProfile {
  username: string;
  joined: string;
  rating: number;
  reviews: number;
  trades: number;
  strikes: number;
  bio: string;
  country?: string;
  countryName?: string;
  specialisations?: TCG[];
  buyerRating?: number;
  buyerReviews?: number;
  sellerRating?: number;
  sellerReviews?: number;
}

export function displayId(o: BuyOrder) {
  const cond = o.condition === "graded" && o.gradingCo && o.minGrade != null
    ? gradeLabel(o.gradingCo, o.minGrade, o.gradeVariant)
    : `Raw${o.rawGrade ? ` ${o.rawGrade}` : ""}`;
  return `${o.cardName} ${cond} · #${o.shortId}`;
}

export const PACKAGE_PHOTO_THRESHOLD = 5000;

export function isTrustedSeller(p: UserProfile): boolean {
  const rating = p.sellerRating ?? p.rating ?? 0;
  const reviews = p.sellerReviews ?? p.reviews ?? 0;
  return rating >= 4.95 && reviews >= 100;
}
export function isTrustedBuyer(p: UserProfile): boolean {
  const rating = p.buyerRating ?? 0;
  const reviews = p.buyerReviews ?? 0;
  return rating >= 4.95 && reviews >= 20;
}

// Platform is currently free — no platform fee on transactions.
export const PLATFORM_FEE_RATE = 0;
export function calcFee(card: number, shipping: number) {
  const total = card + shipping;
  return { total, fee: 0, sellerReceives: total };
}

export const STATUS_META: Record<OrderStatus, { label: string; tone: string }> = {
  open: { label: "Open", tone: "bg-primary/15 text-primary border border-primary/30" },
  offers: { label: "Offers", tone: "bg-warning/15 text-warning border border-warning/30" },
  accepted: { label: "Accepted", tone: "bg-success/15 text-success border border-success/30" },
  shipped: { label: "Shipped", tone: "bg-blue-500/15 text-blue-300 border border-blue-500/30" },
  delivered: { label: "Delivered", tone: "bg-success/15 text-success border border-success/30" },
  released: { label: "Released", tone: "bg-success/20 text-success border border-success/40" },
  disputed: { label: "Disputed", tone: "bg-destructive/15 text-destructive border border-destructive/30" },
  cancelled: { label: "Cancelled", tone: "bg-muted text-muted-foreground border border-border" },
};

export function flagEmoji(code?: string) {
  if (!code || code.length !== 2) return "";
  const A = 0x1f1e6;
  const up = code.toUpperCase();
  return String.fromCodePoint(A + (up.charCodeAt(0) - 65), A + (up.charCodeAt(1) - 65));
}

export const COUNTRIES: { code: string; name: string }[] = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "IE", name: "Ireland" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "PT", name: "Portugal" },
  { code: "JP", name: "Japan" },
  { code: "KR", name: "South Korea" },
  { code: "SG", name: "Singapore" },
  { code: "HK", name: "Hong Kong" },
  { code: "MX", name: "Mexico" },
  { code: "BR", name: "Brazil" },
  { code: "AR", name: "Argentina" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Demo orders — the ONLY hardcoded data the platform ships with.
// These three orders are pinned at the top of Browse for new-user onboarding
// and resolved by /demo/:id. They never touch Supabase.
// ─────────────────────────────────────────────────────────────────────────────



export const DEMO_ORDERS: BuyOrder[] = [
  { id: "demo_pkm", shortId: "DEMO1", buyer: "demo_buyer", tcg: "Pokémon", cardName: "Charizard (Demo)", set: "Base Set", cardNumber: "4/102", condition: "graded", gradingCo: "PSA", minGrade: 9, targetPrice: 4200, marketValue: 4450, offerCount: 3, status: "open", createdAt: "Demo", imageSeed: 101, imageUrl: demoCharizard.url, preferredShipping: "Express Tracked", signatureRequired: true, isDemo: true },
  { id: "demo_mtg", shortId: "DEMO2", buyer: "demo_buyer", tcg: "Magic: The Gathering", cardName: "Liliana of the Veil (Demo)", set: "Innistrad", cardNumber: "105/264", condition: "raw", rawGrade: "NM", targetPrice: 95, marketValue: 110, offerCount: 2, status: "open", createdAt: "Demo", imageSeed: 102, imageUrl: demoLiliana.url, preferredShipping: "Standard Tracked", signatureRequired: false, isDemo: true },
  { id: "demo_op", shortId: "DEMO3", buyer: "demo_buyer", tcg: "One Piece", cardName: "2022 One Piece OP01-Romance Dawn #003 Monkey D. Luffy (Demo)", set: "OP01 Romance Dawn", cardNumber: "OP01-003", condition: "graded", gradingCo: "PSA", minGrade: 8, targetPrice: 850, marketValue: 920, offerCount: 1, status: "open", createdAt: "Demo", imageSeed: 103, imageUrl: demoLuffy.url, preferredShipping: "Standard Tracked", signatureRequired: true, isDemo: true },
];

export function getDemoOrder(id: string): BuyOrder | undefined {
  return DEMO_ORDERS.find((o) => o.id === id);
}
